/**
 * Focus Trap Utility for Dialog Accessibility
 * Implements WCAG 2.1.1 (Keyboard) and 2.4.3 (Focus Order)
 */

export interface FocusTrapOptions {
    /** The container element that contains the focusable elements */
    container: HTMLElement;
    /** Element to return focus to when trap is deactivated */
    returnFocusElement?: HTMLElement;
    /** Callback when focus trap is activated */
    onActivate?: () => void;
    /** Callback when focus trap is deactivated */
    onDeactivate?: () => void;
    /** Initial element to focus when trap activates */
    initialFocus?: HTMLElement;
    /** Whether to enforce reading order within the dialog */
    enforceReadingOrder?: boolean;
    /** Custom reading order elements (overrides automatic detection) */
    readingOrderElements?: HTMLElement[];
}

export class FocusTrap {
    private container: HTMLElement;
    private returnFocusElement?: HTMLElement;
    private onActivate?: () => void;
    private onDeactivate?: () => void;
    private initialFocus?: HTMLElement;
    private previousActiveElement: Element | null = null;
    private keydownHandler: (e: KeyboardEvent) => void;
    private isActive: boolean = false;
    private enforceReadingOrderFlag: boolean = false;
    private readingOrderElements?: HTMLElement[];

    constructor(options: FocusTrapOptions) {
        this.container = options.container;
        this.returnFocusElement = options.returnFocusElement;
        this.onActivate = options.onActivate;
        this.onDeactivate = options.onDeactivate;
        this.initialFocus = options.initialFocus;
        this.enforceReadingOrderFlag = options.enforceReadingOrder || false;
        this.readingOrderElements = options.readingOrderElements;
        this.previousActiveElement = document.activeElement;

        this.keydownHandler = this.handleKeydown.bind(this);
    }

    /**
     * Get all focusable elements within the container
     */
    private getFocusableElements(): HTMLElement[] {
        const selector = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
            '[contenteditable="true"]'
        ].join(', ');

        const elements = Array.from(this.container.querySelectorAll(selector)) as HTMLElement[];

        // Filter out elements that are hidden or disabled
        return elements.filter(el => {
            const style = window.getComputedStyle(el);
            return (
                el.offsetWidth > 0 &&
                el.offsetHeight > 0 &&
                style.display !== 'none' &&
                style.visibility !== 'hidden' &&
                style.opacity !== '0' &&
                !el.hasAttribute('disabled') &&
                el.getAttribute('tabindex') !== '-1'
            );
        });
    }

    /**
     * Handle keyboard events for focus trapping
     */
    private handleKeydown(event: KeyboardEvent): void {
        const focusableElements = this.getFocusableElements();

        if (focusableElements.length === 0) {
            event.preventDefault();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const currentElement = document.activeElement as HTMLElement;

        // Handle Tab and Shift+Tab
        if (event.key === 'Tab') {
            if (event.shiftKey) {
                // Shift+Tab: going backwards
                if (currentElement === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                }
            } else {
                // Tab: going forwards
                if (currentElement === lastElement) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        }
        // Handle arrow keys for screen reader virtual cursor navigation
        else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            // Prevent arrow keys from escaping the dialog
            if (!this.container.contains(currentElement)) {
                event.preventDefault();
                firstElement.focus();
            } else {
                // Allow arrow navigation within the dialog but prevent escape
                const currentIndex = focusableElements.indexOf(currentElement);
                if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
                    // Allow normal arrow navigation to next element
                    return;
                } else if (currentIndex === focusableElements.length - 1) {
                    // Prevent escape from last element
                    event.preventDefault();
                    firstElement.focus();
                } else {
                    // Not on a focusable element, move to first
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        }
        else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            // Prevent arrow keys from escaping the dialog
            if (!this.container.contains(currentElement)) {
                event.preventDefault();
                lastElement.focus();
            } else {
                // Allow arrow navigation within the dialog but prevent escape
                const currentIndex = focusableElements.indexOf(currentElement);
                if (currentIndex > 0) {
                    // Allow normal arrow navigation to previous element
                    return;
                } else if (currentIndex === 0) {
                    // Prevent escape from first element
                    event.preventDefault();
                    lastElement.focus();
                } else {
                    // Not on a focusable element, move to last
                    event.preventDefault();
                    lastElement.focus();
                }
            }
        }
        // Handle Home and End keys for screen readers
        else if (event.key === 'Home') {
            event.preventDefault();
            firstElement.focus();
        }
        else if (event.key === 'End') {
            event.preventDefault();
            lastElement.focus();
        }
        // Handle PageUp and PageDown for screen readers
        else if (event.key === 'PageUp' || event.key === 'PageDown') {
            event.preventDefault();
            // Stay within dialog - you could implement page-by-page navigation here
            // For now, just prevent escape
        }
    }

    /**
     * Activate the focus trap
     */
    activate(): void {
        if (this.isActive) return;

        this.isActive = true;

        // Add keyboard event listener
        document.addEventListener('keydown', this.keydownHandler, true);

        // Enforce reading order if enabled
        if (this.enforceReadingOrderFlag) {
            this.enforceReadingOrder();
        }

        // Focus initial element or first focusable element
        const focusableElements = this.getFocusableElements();
        if (focusableElements.length > 0) {
            const targetElement = this.initialFocus || focusableElements[0];

            // Use setTimeout to ensure focus is set after DOM updates
            setTimeout(() => {
                targetElement.focus();
            }, 0);
        }

        // Call activation callback
        this.onActivate?.();
    }

    /**
     * Deactivate the focus trap
     */
    deactivate(): void {
        if (!this.isActive) return;

        this.isActive = false;

        // Remove keyboard event listener
        document.removeEventListener('keydown', this.keydownHandler, true);

        // Clean up reading order if it was enforced
        if (this.enforceReadingOrderFlag) {
            this.cleanupReadingOrder();
        }

        // Return focus to the return element or previous active element
        const returnElement = this.returnFocusElement || this.previousActiveElement as HTMLElement;
        if (returnElement && typeof returnElement.focus === 'function') {
            setTimeout(() => {
                returnElement.focus();
            }, 0);
        }

        // Call deactivation callback
        this.onDeactivate?.();
    }

    /**
     * Check if focus trap is active
     */
    get active(): boolean {
        return this.isActive;
    }

    /**
     * Update the container element (useful for dynamic content)
     */
    updateContainer(container: HTMLElement): void {
        this.container = container;

        // If trap is active, re-focus to ensure proper containment
        if (this.isActive) {
            const focusableElements = this.getFocusableElements();
            if (focusableElements.length > 0) {
                const currentFocus = document.activeElement as HTMLElement;

                // Check if current focus is still in the container
                if (!this.container.contains(currentFocus)) {
                    focusableElements[0].focus();
                }
            }
        }
    }

    /**
     * Get reading order elements within the container
     * This ensures proper semantic order for screen readers
     */
    private getReadingOrderElements(): HTMLElement[] {
        // If custom reading order is provided, use it
        if (this.readingOrderElements) {
            return this.readingOrderElements;
        }

        // Otherwise, detect elements in reading order
        const readingOrderSelector = [
            'h1, h2, h3, h4, h5, h6',
            'p, div, span',
            'label',
            'button, [role="button"]',
            'input, select, textarea',
            'a[href]',
            '[tabindex]:not([tabindex="-1"])',
            '[role="menuitem"]',
            '[role="option"]',
            '[role="treeitem"]'
        ].join(', ');

        const elements = Array.from(this.container.querySelectorAll(readingOrderSelector)) as HTMLElement[];

        // Filter out hidden elements and those that should be skipped
        return elements.filter(el => {
            const style = window.getComputedStyle(el);
            const isHidden = style.display === 'none' ||
                style.visibility === 'hidden' ||
                style.opacity === '0' ||
                el.getAttribute('aria-hidden') === 'true';

            // Skip decorative elements
            const isDecorative = el.getAttribute('role') === 'presentation' ||
                el.getAttribute('aria-hidden') === 'true' ||
                el.classList.contains('sr-only');

            return !isHidden && !isDecorative;
        });
    }

    /**
     * Enforce proper reading order within the dialog
     */
    private enforceReadingOrder(): void {
        // Add aria-flowto to establish reading order if needed
        const readingElements = this.getReadingOrderElements();

        readingElements.forEach((element, index) => {
            // Remove existing aria-flowto
            element.removeAttribute('aria-flowto');

            // Add aria-flowto to next element in reading order
            if (index < readingElements.length - 1) {
                const nextElement = readingElements[index + 1];
                const nextId = nextElement.id || `dialog-element-${index}`;

                // Ensure next element has an ID
                if (!nextElement.id) {
                    nextElement.id = nextId;
                }

                element.setAttribute('aria-flowto', nextId);
            }
        });
    }

    /**
     * Clean up reading order attributes
     */
    private cleanupReadingOrder(): void {
        const readingElements = this.getReadingOrderElements();
        readingElements.forEach(element => {
            element.removeAttribute('aria-flowto');
        });
    }

    /**
     * Clean up and destroy the focus trap
     */
    destroy(): void {
        this.deactivate();
        this.cleanupReadingOrder();
        this.previousActiveElement = null;
    }
}

/**
 * React Hook for Focus Trap
 */
export function useFocusTrap(options: FocusTrapOptions) {
    const focusTrapRef = React.useRef<FocusTrap | null>(null);

    React.useEffect(() => {
        if (options.container) {
            focusTrapRef.current = new FocusTrap(options);
            focusTrapRef.current.activate();
        }

        return () => {
            focusTrapRef.current?.destroy();
        };
    }, [options.container]);

    return focusTrapRef.current;
}

// Import React for the hook
import React from 'react';
